import { Controller } from "@hotwired/stimulus"

// Shared multi-step questionnaire wizard behavior for the ABLE and Trump
// Account matcher controllers. Subclasses declare their own `static targets`
// and add flow-specific validators; the common step navigation, validation
// helpers, and ineligibility handling live here.
export default class BaseMatcherController extends Controller {
  connect() {
    this.requiredTextTargets.forEach((text) => (text.style.display = "none"))
    this.ineligibleContentTargets.forEach((el) => (el.style.display = "none"))
    this.stepHistory = [1]
    this.goToStep(1)
  }

  nextStep(event) {
    event?.preventDefault()
    const numSteps = this.stepContentTargets.length

    if (this.currentStep >= numSteps) {
      this.submitQuestionnaire()
    } else {
      this.stepHistory.push(this.currentStep + 1)
      this.goToStep(this.currentStep + 1)
    }
  }

  prevStep(event) {
    event?.preventDefault()
    // Never pop past the first step — that would leave an empty history and
    // navigate to an undefined step, blanking the wizard.
    if (this.stepHistory.length <= 1) return

    this.stepHistory.pop()
    // Hide any ineligible content
    this.ineligibleContentTargets.forEach((el) => (el.style.display = "none"))
    this.goToStep(this.stepHistory[this.stepHistory.length - 1])
  }

  goToStepWithKey(stepKey) {
    const index = this.stepContentTargets.findIndex(
      (element) => element.dataset.stepKey === stepKey,
    )
    if (index >= 0) {
      this.stepHistory.push(index + 1)
      this.goToStep(index + 1)
    }
  }

  goToStep(step) {
    this.currentStep = step
    this.togglePreviousLink()

    this.stepContentTargets.forEach((element, index) => {
      const isStep = this.currentStep === index + 1
      element.style.display = isStep ? "block" : "none"

      if (isStep) {
        this.stepTextTarget.innerText = element.dataset.stepText || ""
      }
    })

    window.scrollTo(0, 0)
  }

  showIneligible(reason) {
    // Hide all step content
    this.stepContentTargets.forEach((el) => (el.style.display = "none"))
    // Hide Previous button and step counter
    this.previousLinkTarget.style.display = "none"
    this.stepTextTarget.innerText = ""

    // Show the matching ineligible result
    const target = this.ineligibleContentTargets.find(
      (el) => el.dataset.ineligibleReason === reason,
    )
    if (target) {
      target.style.display = "block"
    }
    window.scrollTo(0, 0)
  }

  togglePreviousLink() {
    this.previousLinkTarget.style.display =
      this.stepHistory.length > 1 ? "flex" : "none"
  }

  // Generic input validation (text/select inputs). Subclasses may override to
  // add radio-required handling.
  validate(event) {
    event.preventDefault()
    const stepContent = this.stepFor(event)
    if (!stepContent) return

    if (this.checkFieldValidity(stepContent)) {
      this.nextStep()
    }
  }

  // Toggle the is-invalid class on each text/select control in the step and
  // return whether they are all valid.
  checkFieldValidity(stepContent) {
    let valid = true
    stepContent.querySelectorAll("input, select").forEach((control) => {
      if (!control.validity.valid) {
        control.classList.add("is-invalid")
        valid = false
      } else {
        control.classList.remove("is-invalid")
      }
    })
    return valid
  }

  validateRadio(event) {
    event.preventDefault()
    const stepContent = this.stepFor(event)
    if (!stepContent) return
    const requiredText = this.requiredTextFor(stepContent)
    const selectedValue = this.selectedRadioValue(stepContent)

    if (selectedValue?.length > 0) {
      this.toggleRequired(requiredText, false)
      this.nextStep()
    } else {
      this.toggleRequired(requiredText, true)
    }
  }

  // Investment approach — if not_sure, go to personal saving style; otherwise submit
  validateInvestmentApproach(event) {
    event.preventDefault()
    const stepContent = this.stepFor(event)
    if (!stepContent) return
    const requiredText = this.requiredTextFor(stepContent)
    const selectedValue = this.selectedRadioValue(stepContent)

    if (!selectedValue) {
      this.toggleRequired(requiredText, true)
      return
    }

    this.toggleRequired(requiredText, false)
    if (selectedValue === "not_sure") {
      this.goToStepWithKey("personalSavingStyle")
    } else {
      this.submitQuestionnaire()
    }
  }

  isVisible(el) {
    return !!el && el.offsetParent !== null
  }

  submitQuestionnaire() {
    this.element.submit()
  }

  // --- Shared helpers -------------------------------------------------------

  // The step element that contains the event target.
  stepFor(event) {
    return this.stepContentTargets.find((target) =>
      target.contains(event.target),
    )
  }

  // The "Required" message element within a step, if present.
  requiredTextFor(stepContent) {
    return this.requiredTextTargets.find((text) => stepContent?.contains(text))
  }

  // The value of the checked radio within a step, if any.
  selectedRadioValue(stepContent) {
    return stepContent?.querySelector("input[type='radio']:checked")?.value
  }

  // Safely show/hide a (possibly absent) "Required" message.
  toggleRequired(requiredText, visible) {
    if (requiredText) {
      requiredText.style.display = visible ? "block" : "none"
    }
  }
}
